import {
  useEffect,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction
} from 'react';
import type { Location } from 'react-router-dom';
import { prefersReducedMotion } from '../../../lib/motion';
import { triggerHaptic } from '../../../lib/haptics';
import {
  CANCEL_MIN_MS,
  CANCEL_MS,
  CB_OUT_IOS,
  COMMIT_DISTANCE_RATIO,
  DIM_MAX,
  DIRECTION_LOCK_PX,
  EDGE_ZONE_PX,
  FLICK_VELOCITY,
  historyIdx,
  MIN_FLICK_PX,
  PARALLAX,
  POP_MIN_MS,
  POP_MS,
  SETTLE_GUARD_MS,
  SHADOW
} from './constants';
import type { Drag } from './types';

export function useEdgeSwipeBack(
  interactive: boolean,
  rootRef: MutableRefObject<HTMLDivElement | null>,
  nodesRef: MutableRefObject<Map<string, HTMLDivElement>>,
  dimRef: MutableRefObject<HTMLDivElement | null>,
  curKeyRef: MutableRefObject<string>,
  navRef: MutableRefObject<(delta: number) => void>,
  busyRef: MutableRefObject<boolean>,
  animatingRef: MutableRefObject<boolean>,
  committingRef: MutableRefObject<boolean>,
  historyRef: MutableRefObject<Map<number, Location>>,
  setDrag: Dispatch<SetStateAction<Drag | null>>
) {
  useEffect(() => {
    if (!interactive) return;
    const root = rootRef.current;
    if (!root) return;

    let tracking = false;
    let engaged = false;
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let width = 0;
    let targetKey: string | null = null;

    const clearStyles = (...nodes: (HTMLElement | null)[]) => {
      for (const n of nodes) {
        if (!n) continue;
        n.style.transition = '';
        n.style.transform = '';
        n.style.willChange = '';
        n.style.boxShadow = '';
        n.style.opacity = '';
      }
    };

    const settle = (commit: boolean, fromX: number) => {
      animatingRef.current = true;
      const top = nodesRef.current.get(curKeyRef.current) ?? null;
      const under = targetKey ? (nodesRef.current.get(targetKey) ?? null) : null;
      const dim = dimRef.current;
      const w = width || root.clientWidth || window.innerWidth;

      const land = () => {
        if (commit) {
          committingRef.current = true;
          navRef.current(-1);
          // Hold the committed styles until the new (revealed) route has rendered as
          // the solo layer — it reuses the same DOM node (same key), so there's no
          // flash — then clear everything.
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              clearStyles(top, under, dim);
              committingRef.current = false;
              animatingRef.current = false;
              setDrag(null);
            })
          );
        } else {
          clearStyles(top, under, dim);
          animatingRef.current = false;
          setDrag(null);
        }
      };

      if (commit) void triggerHaptic('medium', { force: true });
      if (prefersReducedMotion() || !top || !under) {
        land();
        return;
      }

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        top.removeEventListener('transitionend', onEnd);
        land();
      };
      const onEnd = (e: TransitionEvent) => {
        if (e.target === top && e.propertyName === 'transform') finish();
      };
      // Scale the settle by how far is left to travel and how fast the finger was
      // moving: a near-complete swipe (or a flick) lands quickly instead of crawling
      // the last pixels at a fixed duration; a slow release near the start eases the
      // full page distance. Clamped so it never feels abrupt or sluggish.
      const clampedFrom = Math.max(0, Math.min(fromX, w));
      const dist = commit ? Math.max(0, w - clampedFrom) : clampedFrom;
      const speed = Math.min(Math.max(Math.abs(velocity), 0.8), 3.5); // px/ms
      const ms = Math.round(
        commit
          ? Math.min(POP_MS, Math.max(POP_MIN_MS, dist / speed))
          : Math.min(CANCEL_MS, Math.max(CANCEL_MIN_MS, dist / speed))
      );
      top.addEventListener('transitionend', onEnd);
      requestAnimationFrame(() => {
        top.style.transition = `transform ${ms}ms ${CB_OUT_IOS}`;
        under.style.transition = `transform ${ms}ms ${CB_OUT_IOS}`;
        if (dim) dim.style.transition = `opacity ${ms}ms ${CB_OUT_IOS}`;
        if (commit) {
          top.style.transform = `translateX(${w}px)`;
          under.style.transform = 'translateX(0px)';
          if (dim) dim.style.opacity = '0';
        } else {
          top.style.transform = 'translateX(0px)';
          under.style.transform = `translateX(${-PARALLAX * w}px)`;
          if (dim) dim.style.opacity = String(DIM_MAX);
        }
      });
      window.setTimeout(finish, ms + SETTLE_GUARD_MS + 20);
    };

    const onStart = (e: TouchEvent) => {
      if (busyRef.current || animatingRef.current || committingRef.current) return;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_ZONE_PX || historyIdx() <= 0) return;
      if ((e.target as Element | null)?.closest?.('[data-no-swipe]')) return;
      // We can only reveal a previous screen we actually have a location for (i.e.
      // navigated to it in this session). On a cold deep-link we don't, so bow out
      // and let nothing happen rather than slide over a blank page.
      const target = historyRef.current.get(historyIdx() - 1) ?? null;
      if (!target) return;
      tracking = true;
      engaged = false;
      startX = lastX = t.clientX;
      startY = t.clientY;
      lastT = performance.now();
      velocity = 0;
      width = root.clientWidth || window.innerWidth;
      targetKey = target.key;
      // Mount the destination beneath now, so it's painted by the time the finger
      // reveals it (a layout effect parks it at the parallax start position).
      setDrag({ target });
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!engaged) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > DIRECTION_LOCK_PX) {
          tracking = false;
          clearStyles(nodesRef.current.get(curKeyRef.current) ?? null);
          setDrag(null);
          return;
        }
        if (dx > DIRECTION_LOCK_PX) {
          engaged = true;
          void triggerHaptic('light', { force: true });
        } else {
          return;
        }
      }
      if (e.cancelable) e.preventDefault();
      const now = performance.now();
      const x = Math.max(0, dx);
      const p = Math.min(1, x / width);
      if (now > lastT) velocity = (t.clientX - lastX) / (now - lastT);
      lastX = t.clientX;
      lastT = now;
      const top = nodesRef.current.get(curKeyRef.current) ?? null;
      const under = targetKey ? (nodesRef.current.get(targetKey) ?? null) : null;
      const dim = dimRef.current;
      if (top) {
        top.style.transition = 'none';
        top.style.transform = `translateX(${x}px)`;
        top.style.boxShadow = SHADOW;
      }
      if (under) {
        under.style.transition = 'none';
        under.style.transform = `translateX(${-(1 - p) * PARALLAX * width}px)`;
      }
      if (dim) {
        dim.style.transition = 'none';
        dim.style.opacity = String((1 - p) * DIM_MAX);
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      const wasEngaged = engaged;
      tracking = false;
      engaged = false;
      if (!wasEngaged) {
        clearStyles(nodesRef.current.get(curKeyRef.current) ?? null);
        setDrag(null);
        return;
      }
      const endX = e.changedTouches[0]?.clientX ?? lastX;
      const dx = Math.max(0, endX - startX);
      const commit =
        dx > width * COMMIT_DISTANCE_RATIO || (dx > MIN_FLICK_PX && velocity > FLICK_VELOCITY);
      settle(commit, dx);
    };

    root.addEventListener('touchstart', onStart, { passive: true });
    root.addEventListener('touchmove', onMove, { passive: false });
    root.addEventListener('touchend', onEnd, { passive: true });
    root.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      root.removeEventListener('touchstart', onStart);
      root.removeEventListener('touchmove', onMove);
      root.removeEventListener('touchend', onEnd);
      root.removeEventListener('touchcancel', onEnd);
    };
  }, [
    interactive,
    rootRef,
    nodesRef,
    dimRef,
    curKeyRef,
    navRef,
    busyRef,
    animatingRef,
    committingRef,
    historyRef,
    setDrag
  ]);
}
